/**
 * AreaManager - Manages area CRUD operations
 *
 * Responsibilities:
 * - Add, delete, duplicate areas
 * - Update area UI (titles, buttons)
 * - Handle shape type selection
 * - Handle calculation method changes
 * - Update dimension fields based on shape type
 */

import { t } from "./translations.js";

export default class AreaManager {
  constructor(rootElement, templateManager) {
    this.root = rootElement;
    this.templateManager = templateManager;
    this.areas = [];
    this.areaCounter = 1;

    this.selectors = {
      area: '[data-substrate-calculator="area"]',
      addAreaBtn: '[data-substrate-calculator="add-area"]',
      duplicateBtn: '[data-substrate-calculator="duplicate"]',
      deleteBtn: '[data-substrate-calculator="delete"]',
      shapeTypeBtn: '[data-substrate-calculator="shape-type"]',
      dimensionInput: '[data-substrate-calculator^="dimension-"]',
      calculationMethod: '[data-substrate-calculator="calculation-method"]',
      areaTitle: '[data-substrate-calculator="area-title"]',
      areaContainer: '[data-substrate-calculator="area-container"]',
    };
  }

  /**
   * Initialize areas from DOM
   */
  initializeAreas() {
    const areaElements = this.root.querySelectorAll(this.selectors.area);

    areaElements.forEach((areaElement) => {
      const areaId = parseInt(areaElement.dataset.areaId || "0");
      const shapeType = areaElement.dataset.shapeType || "box";
      const withDrainage = areaElement.dataset.withDrainage === "true";

      // Set unique names for radio buttons (for existing areas from HTML)
      const calcMethodRadios = areaElement.querySelectorAll(
        'input[name="calculation_method"]',
      );
      calcMethodRadios.forEach((radio) => {
        radio.name = `calculation_method_${areaId}`;
      });

      const drainageRadios = areaElement.querySelectorAll('input[name="drainage"]');
      drainageRadios.forEach((radio) => {
        radio.name = `drainage_${areaId}`;
      });

      const volumeTypeRadios = areaElement.querySelectorAll('input[name="known_volume_type"]');
      volumeTypeRadios.forEach((radio) => {
        radio.name = `known_volume_type_${areaId}`;
      });

      // Show correct shape image
      this.updateShapeImage(areaElement, shapeType);

      // Update measure option visibility
      this.updateMeasureOptionVisibility(areaElement, shapeType);

      this.areas.push({
        id: areaId || this.areaCounter++,
        element: areaElement,
        shapeType: shapeType,
        calculationMethod: "measure",
        withDrainage: withDrainage,
      });
    });

    // Update counter to avoid ID conflicts with existing areas
    if (this.areas.length > 0) {
      const maxId = Math.max(...this.areas.map(a => a.id));
      this.areaCounter = maxId + 1;
    }

    this.updateAreaTitles();
    this.updateDeleteButtonsVisibility();

    // Setup focus listeners for auto-highlighting
    this.setupAreaFocusListeners();
  }

  /**
   * Setup focus listeners to highlight area when editing
   */
  setupAreaFocusListeners() {
    this.areas.forEach((areaData) => {
      this.setupAreaFocusListener(areaData);
    });
  }

  /**
   * Setup focus listener for a single area
   *
   * @param {Object} areaData - Area data object
   */
  setupAreaFocusListener(areaData) {
    const areaElement = areaData.element;

    // Listen for focus on any input within the area
    areaElement.addEventListener('focusin', (e) => {
      const area = e.target.closest(this.selectors.area);
      if (area) {
        const areaId = parseInt(area.dataset.areaId || "0");
        const index = this.areas.findIndex((a) => a.id === areaId);
        if (index !== -1) {
          this.highlightArea(index);
        }
      }
    }, true);
  }

  /**
   * Add a new area
   *
   * @returns {Object} New area data
   */
  addArea() {
    const areaFragment = this.templateManager.clone("area");
    if (!areaFragment) return null;

    const newArea = areaFragment.querySelector(this.selectors.area);
    if (!newArea) return null;

    const areaId = this.areaCounter++;
    newArea.dataset.areaId = areaId;

    // Set unique names for radio buttons
    const calcMethodRadios = newArea.querySelectorAll(
      'input[name="calculation_method"]',
    );
    calcMethodRadios.forEach((radio) => {
      radio.name = `calculation_method_${areaId}`;
    });

    const drainageRadios = newArea.querySelectorAll('input[name="drainage"]');
    drainageRadios.forEach((radio) => {
      radio.name = `drainage_${areaId}`;
    });

    const volumeTypeRadios = newArea.querySelectorAll('input[name="known_volume_type"]');
    volumeTypeRadios.forEach((radio) => {
      radio.name = `known_volume_type_${areaId}`;
    });

    const areaContainer = this.root.querySelector(this.selectors.areaContainer);
    const buttonsContainer = areaContainer?.querySelector(
      '[data-substrate-calculator="buttons-container"]',
    );

    if (areaContainer) {
      if (buttonsContainer) {
        // Insert before buttons-container
        areaContainer.insertBefore(newArea, buttonsContainer);
      } else {
        // Fallback: append to end
        areaContainer.appendChild(newArea);
      }
    }

    const titleElement = newArea.querySelector(this.selectors.areaTitle);
    if (titleElement) {
      titleElement.textContent = t("area_title", this.areas.length + 1);
    }

    // Set default shape type button style (box is selected by default)
    const boxButton = newArea.querySelector('[data-shape-type="box"]');
    if (boxButton) {
      // Already has correct classes from template
    }

    // Set default shape type on the area element
    newArea.dataset.shapeType = "box";

    // Show default shape image (box)
    this.updateShapeImage(newArea, "box");

    // Update measure option visibility for box shape
    this.updateMeasureOptionVisibility(newArea, "box");

    // Set default calculation method (measure)
    const measureRadio = newArea.querySelector('input[value="measure"]');
    if (measureRadio) {
      measureRadio.checked = true;
    }

    // Set default drainage (with-drainage)
    const withDrainageRadio = newArea.querySelector(
      'input[value="with-drainage"]',
    );
    if (withDrainageRadio) {
      withDrainageRadio.checked = true;
    }

    const newAreaData = {
      id: areaId,
      element: newArea,
      shapeType: "box",
      calculationMethod: "measure",
      withDrainage: true,
    };

    this.areas.push(newAreaData);
    this.updateAreaTitles();
    this.updateDeleteButtonsVisibility();

    // Setup focus listener for the new area
    this.setupAreaFocusListener(newAreaData);

    // Highlight the new area
    const newIndex = this.areas.length - 1;
    this.highlightArea(newIndex);

    // Scroll to the new area
    newArea.scrollIntoView({ behavior: "smooth", block: "center" });

    return newAreaData;
  }

  /**
   * Delete an area
   *
   * @param {HTMLElement} deleteBtn - Delete button element
   */
  deleteArea(deleteBtn) {
    if (this.areas.length <= 1) {
      return;
    }

    const area = deleteBtn.closest(this.selectors.area);
    if (!area) return;

    const areaId = parseInt(area.dataset.areaId || "0");
    const areaIndex = this.areas.findIndex((a) => a.id === areaId);

    if (areaIndex > -1) {
      this.areas.splice(areaIndex, 1);
    }

    // Get previous area to scroll to
    const previousArea = area.previousElementSibling;

    area.remove();
    this.updateAreaTitles();
    this.updateDeleteButtonsVisibility();

    // Scroll to previous area if exists
    if (previousArea && previousArea.matches(this.selectors.area)) {
      previousArea.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Duplicate an area
   *
   * @param {HTMLElement} duplicateBtn - Duplicate button element
   */
  duplicateArea(duplicateBtn) {
    const area = duplicateBtn.closest(this.selectors.area);
    if (!area) return;

    const newArea = this.addArea();
    if (!newArea) return;

    // Copy shape type
    const originalShapeType = area.dataset.shapeType || "box";
    const originalWithDrainage = area.dataset.withDrainage === "true";

    newArea.shapeType = originalShapeType;
    newArea.withDrainage = originalWithDrainage;

    newArea.element.dataset.shapeType = originalShapeType;
    newArea.element.dataset.withDrainage = originalWithDrainage;

    // Copy dimension values
    const originalInputs = area.querySelectorAll(this.selectors.dimensionInput);
    const newInputs = newArea.element.querySelectorAll(
      this.selectors.dimensionInput,
    );

    originalInputs.forEach((input, index) => {
      if (newInputs[index]) {
        newInputs[index].value = input.value;
      }
    });

    // Copy calculation method
    const originalMethod = area.querySelector(
      'input[name^="calculation_method"]:checked',
    );
    const newMethod = newArea.element.querySelector(
      `input[value="${originalMethod?.value}"]`,
    );
    if (newMethod) {
      newMethod.checked = true;
    }

    // Copy drainage selection
    const originalDrainage = area.querySelector(
      'input[name^="drainage"]:checked',
    );
    const newDrainageRadio = newArea.element.querySelector(
      `input[value="${originalDrainage?.value}"]`,
    );
    if (newDrainageRadio) {
      newDrainageRadio.checked = true;
    }

    // Copy known volume type selection (liters vs cubic meters)
    const originalVolumeType = area.querySelector(
      'input[name^="known_volume_type"]:checked',
    );
    const newVolumeTypeRadio = newArea.element.querySelector(
      `input[value="${originalVolumeType?.value}"]`,
    );
    if (newVolumeTypeRadio) {
      newVolumeTypeRadio.checked = true;
      // Trigger visibility update
      this.changeKnownVolumeType(newVolumeTypeRadio);
    }

    // Update shape type selection
    const targetShapeButton = newArea.element.querySelector(
      `[data-shape-type="${originalShapeType}"]`,
    );
    if (targetShapeButton) {
      this.selectShapeType(targetShapeButton, true);
    }

    const titleElement = newArea.element.querySelector(
      this.selectors.areaTitle,
    );
    if (titleElement) {
      titleElement.textContent = t("area_title", this.areas.length);
    }

    // The new area is already highlighted by addArea, and scrolled into view
  }

  /**
   * Select shape type for an area
   *
   * @param {HTMLElement} shapeTypeBtn - Shape type button
   * @param {boolean} silent - Whether to suppress area data update
   */
  selectShapeType(shapeTypeBtn, silent = false) {
    const area = shapeTypeBtn.closest(this.selectors.area);
    if (!area) return;

    const shapeType = shapeTypeBtn.dataset.shapeType;
    const areaId = parseInt(area.dataset.areaId || "0");
    const areaData = this.areas.find((a) => a.id === areaId);

    // Update buttons visual state
    this.resetShapeTypeSelection(area);
    // Add selected styles
    shapeTypeBtn.classList.remove("bg-gray-50", "border-gray-200", "text-gray-700");
    shapeTypeBtn.classList.add(
      "bg-gray-200",
      "border-2",
      "border-brand-secondary-950",
      "font-bold",
      "text-grey-900",
    );

    if (!silent && areaData) {
      areaData.shapeType = shapeType;
    }

    // Update dataset shapeType (used by calculateArea)
    area.dataset.shapeType = shapeType;

    // Update shape image display
    this.updateShapeImage(area, shapeType);

    // Update measure option visibility
    this.updateMeasureOptionVisibility(area, shapeType);

    // Switch to custom calculation if custom shape
    if (shapeType === "custom") {
      this.switchToVolumeMode(area);
    }

    this.updateDimensionFields(area, shapeType);
  }

  updateMeasureOptionVisibility(area, shapeType) {
    const measureOption = area.querySelector(
      '[data-substrate-calculator="measure-option"]',
    );
    if (measureOption) {
      if (shapeType === "custom") {
        measureOption.style.display = "none";
      } else {
        measureOption.style.display = "flex";
      }
    }
  }

  updateShapeImage(area, shapeType) {
    const shapeImages = area.querySelectorAll(".shape-image");
    const targetImage = area.querySelector(`[data-shape-image="${shapeType}"]`);

    shapeImages.forEach((img) => {
      if (img === targetImage) {
        img.classList.remove("opacity-0", "pointer-events-none");
        img.classList.add("opacity-100");
      } else {
        img.classList.remove("opacity-100");
        img.classList.add("opacity-0", "pointer-events-none");
      }
    });
  }

  /**
   * Switch area to volume mode (known volume)
   *
   * @param {HTMLElement} area - Area element
   */
  switchToVolumeMode(area) {
    const areaData = this.getAreaData(area);
    if (!areaData) return;

    areaData.calculationMethod = "known";

    const knownRadio = area.querySelector('input[value="known"]');
    const measureRadio = area.querySelector('input[value="measure"]');

    if (knownRadio && measureRadio) {
      knownRadio.checked = true;
      this.changeCalculationMethod(knownRadio, true);
    }
  }

  /**
   * Update dimension fields based on shape type
   *
   * @param {HTMLElement} area - Area element
   * @param {string} shapeType - Shape type
   */
  updateDimensionFields(area, shapeType) {
    const container = area.querySelector(
      '[data-substrate-calculator="dimensions-container"]',
    );
    if (!container) return;

    const inputs = container.querySelectorAll("input");
    const labelWrappers = container.querySelectorAll("label");
    const labels = container.querySelectorAll("label span");

    // First, reset everything to visible and restore original labels
    labelWrappers.forEach((wrapper) => {
      wrapper.style.display = "flex";
    });

    // Restore original labels for box mode
    const originalLabels = [
      t('depth_label'),
      t('length_label'),
      t('width_label'),
    ];

    // Hide all inputs first
    inputs.forEach((input) => {
      input.style.display = "none";
      input.required = false;
    });

    if (shapeType === "pot") {
      // Show diameter and height (use first two inputs)
      if (inputs[0] && labels[0]) {
        inputs[0].style.display = "block";
        inputs[0].required = true;
        labels[0].textContent = t('diameter_label');
      }
      if (inputs[1] && labels[1]) {
        inputs[1].style.display = "block";
        inputs[1].required = true;
        labels[1].textContent = t('height_label');
      }
      // Hide third input
      if (labelWrappers[2]) {
        labelWrappers[2].style.display = "none";
      }
    } else if (shapeType === "ground") {
      // Show area (m²) and depth (cm)
      if (inputs[0] && labels[0]) {
        inputs[0].style.display = "block";
        inputs[0].required = true;
        labels[0].textContent = t('surface_area_label');
      }
      if (inputs[1] && labels[1]) {
        inputs[1].style.display = "block";
        inputs[1].required = true;
        labels[1].textContent = t('depth_label').replace('a. ', '');
      }
      // Hide third input
      if (labelWrappers[2]) {
        labelWrappers[2].style.display = "none";
      }
    } else if (shapeType === "custom") {
      // Custom: show only middle input for volume
      if (inputs[1] && labels[1]) {
        inputs[1].style.display = "block";
        inputs[1].required = true;
        labels[1].textContent = t('volume_label_l');
      }
      // Hide first and third labels
      if (labelWrappers[0]) {
        labelWrappers[0].style.display = "none";
      }
      if (labelWrappers[2]) {
        labelWrappers[2].style.display = "none";
      }
    } else {
      // Box: show all three inputs with original labels
      if (inputs[0] && labels[0]) {
        inputs[0].style.display = "block";
        inputs[0].required = true;
        labels[0].textContent = originalLabels[0];
      }
      if (inputs[1] && labels[1]) {
        inputs[1].style.display = "block";
        inputs[1].required = true;
        labels[1].textContent = originalLabels[1];
      }
      if (inputs[2] && labels[2]) {
        inputs[2].style.display = "block";
        inputs[2].required = true;
        labels[2].textContent = originalLabels[2];
      }
    }
  }

  /**
   * Change calculation method (measure/known)
   *
   * @param {HTMLInputElement} radioInput - Radio input element
   * @param {boolean} silent - Whether to suppress area data update
   */
  changeCalculationMethod(radioInput, silent = false) {
    const area = radioInput.closest(this.selectors.area);
    if (!area) return;

    const method = radioInput.value;
    const areaData = this.getAreaData(area);

    if (!silent && areaData) {
      areaData.calculationMethod = method;
    }

    // If switching to 'measure' while shape is 'custom', reset to 'box'
    if (method === "measure" && areaData?.shapeType === "custom") {
      areaData.shapeType = "box";
      area.dataset.shapeType = "box"; // Update dataset too
      this.resetShapeTypeSelection(area);
      this.updateMeasureOptionVisibility(area, "box");
    }

    // Toggle visibility of dimension inputs vs volume input
    const dimensionsContainer = area.querySelector(
      '[data-substrate-calculator="dimensions-container"]',
    );
    const knownVolumeContainer = area.querySelector(
      '[data-substrate-calculator="known-volume-container"]',
    );

    if (method === "known") {
      if (dimensionsContainer) dimensionsContainer.style.display = "none";
      if (knownVolumeContainer) knownVolumeContainer.style.display = "block";
    } else {
      if (dimensionsContainer) dimensionsContainer.style.display = "block";
      if (knownVolumeContainer) knownVolumeContainer.style.display = "none";

      // Update dimension fields based on current shape type
      const currentShapeType = areaData?.shapeType || "box";
      this.updateDimensionFields(area, currentShapeType);
    }
  }

  /**
   * Reset shape type buttons visual state
   *
   * @param {HTMLElement} area - Area element
   */
  resetShapeTypeSelection(area) {
    const buttons = area.querySelectorAll(this.selectors.shapeTypeBtn);
    buttons.forEach((btn) => {
      btn.classList.remove(
        "bg-gray-200",
        "border-2",
        "border-brand-secondary-950",
        "font-bold",
        "text-grey-900",
      );
      btn.classList.add("bg-gray-50", "border-gray-200", "text-gray-700");
    });
  }

  /**
   * Update area titles (1, 2, 3, etc.)
   */
  updateAreaTitles() {
    this.areas.forEach((areaData, index) => {
      const titleElement = areaData.element.querySelector(
        this.selectors.areaTitle,
      );
      if (titleElement) {
        titleElement.textContent = t("area_title", index + 1);
      }
    });
  }

  /**
   * Update delete buttons visibility (hide if only one area)
   */
  updateDeleteButtonsVisibility() {
    const deleteButtons = this.root.querySelectorAll(this.selectors.deleteBtn);
    deleteButtons.forEach((btn) => {
      btn.style.display = this.areas.length > 1 ? "flex" : "none";
    });
  }

  /**
   * Get area data by element
   *
   * @param {HTMLElement} area - Area element
   * @returns {Object|null} Area data or null
   */
  getAreaData(area) {
    const areaId = parseInt(area.dataset.areaId || "0");
    return this.areas.find((a) => a.id === areaId) || null;
  }

  /**
   * Get dimensions from area
   *
   * @param {HTMLElement} area - Area element
   * @param {string} shapeType - Shape type
   * @param {string} calculationMethod - Calculation method
   * @returns {Object} Dimensions object
   */
  getDimensions(area, shapeType, calculationMethod) {
    const dimensions = { x: 0, y: 0, z: 0, d: 0, h: 0, volume: 0, volume_m3: 0 };

    if (calculationMethod === "known" || shapeType === "custom") {
      // Check if user selected cubic meters or liters
      const volumeTypeRadio = area.querySelector('input[name^="known_volume_type"]:checked');
      const volumeType = volumeTypeRadio?.value || 'liters';

      if (volumeType === 'cubic_meters') {
        // Get cubic meters input
        const cubicMetersInput = area.querySelector('[data-substrate-calculator="known-cubic-meters"]');
        dimensions.volume_m3 = parseFloat(cubicMetersInput?.value) || 0;
        // Also set volume for compatibility (m³ to liters)
        dimensions.volume = dimensions.volume_m3 * 1000;
      } else {
        // Get volume input (liters)
        const volumeInput = area.querySelector('[data-substrate-calculator="known-volume"]');
        dimensions.volume = parseFloat(volumeInput?.value) || 0;
      }
    } else if (shapeType === "pot") {
      // Pot: get diameter (d) and height (h)
      const dInput = area.querySelector('[data-substrate-calculator="dimension-depth"]');
      const hInput = area.querySelector('[data-substrate-calculator="dimension-length"]');
      dimensions.d = parseFloat(dInput?.value) || 0;
      dimensions.h = parseFloat(hInput?.value) || 0;
    } else if (shapeType === "ground") {
      // Ground: get area (m²) and depth (cm)
      const areaInput = area.querySelector('[data-substrate-calculator="dimension-depth"]');
      const depthInput = area.querySelector('[data-substrate-calculator="dimension-length"]');
      dimensions.area = parseFloat(areaInput?.value) || 0;
      dimensions.depth = parseFloat(depthInput?.value) || 0;
    } else {
      // Box: get depth (z), length (y), width (x)
      const zInput = area.querySelector('[data-substrate-calculator="dimension-depth"]');
      const yInput = area.querySelector('[data-substrate-calculator="dimension-length"]');
      const xInput = area.querySelector('[data-substrate-calculator="dimension-width"]');
      dimensions.z = parseFloat(zInput?.value) || 0; // Głębokość
      dimensions.y = parseFloat(yInput?.value) || 0; // Długość
      dimensions.x = parseFloat(xInput?.value) || 0; // Szerokość
    }

    return dimensions;
  }

  /**
   * Handle known volume type change (liters vs cubic meters)
   *
   * @param {HTMLInputElement} radioInput - Radio input element
   */
  changeKnownVolumeType(radioInput) {
    const area = radioInput.closest(this.selectors.area);
    if (!area) return;

    const volumeType = radioInput.value; // 'liters' or 'cubic_meters'

    // Toggle visibility of input groups
    const litersGroup = area.querySelector('[data-substrate-calculator="liters-input-group"]');
    const cubicMetersGroup = area.querySelector('[data-substrate-calculator="cubic-meters-input-group"]');

    if (volumeType === 'liters') {
      if (litersGroup) litersGroup.style.display = 'flex';
      if (cubicMetersGroup) cubicMetersGroup.style.display = 'none';
    } else {
      if (litersGroup) litersGroup.style.display = 'none';
      if (cubicMetersGroup) cubicMetersGroup.style.display = 'flex';
    }

    // Store the volume type in area dataset
    area.dataset.knownVolumeType = volumeType;
  }

  /**
   * Highlight a specific area with border
   *
   * @param {number} areaIndex - Index of area to highlight (or null to clear all highlights)
   */
  highlightArea(areaIndex = null) {
    // Remove highlight from all areas
    this.areas.forEach((areaData) => {
      areaData.element.classList.remove('border-brand-secondary-950');
      areaData.element.classList.add('border-gray-100');
    });

    // Add highlight to specific area if provided
    if (areaIndex !== null && this.areas[areaIndex]) {
      this.areas[areaIndex].element.classList.remove('border-gray-100');
      this.areas[areaIndex].element.classList.add('border-2', 'border-brand-secondary-950');
    }
  }
}
